import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";

export interface GroupHome {
  id: string;
  name: string;
  address: string | null;
  // 2-letter US state — drives the rule-bundle import state-match guard.
  state: string | null;
  phone: string | null;
  email: string | null;
  created_at: string;
  updated_at: string;
  shift_auto_clock_out_grace_minutes?: number | null;
  // US-098 / US-102 — group-home timekeeping defaults.
  pay_period_cadence?: string | null;
  pay_period_anchor?: string | null;
  timesheet_offset_days?: number | null;
  require_timesheet_approval?: boolean | null;
  facilities?: { count: number }[];
  user_roles?: { count: number }[];
}

export interface Facility {
  id: string;
  name: string;
  address: string | null;
  capacity: number | null;
}

export interface UserFacility {
  id: string;
  name: string;
}

export interface GroupHomeUser {
  id: string;
  employee_id?: string;
  email: string | undefined;
  full_name: string | null;
  role?: {
    id: string;
    name: string;
  };
  role_assignment_id?: string | null;
  facilities?: UserFacility[];
}

export interface GroupHomeDetail {
  id: string;
  name: string;
  address: string | null;
  state: string | null;
  phone: string | null;
  email: string | null;
  created_at: string;
  updated_at: string;
  shift_auto_clock_out_grace_minutes?: number | null;
  // US-098 / US-102 — group-home timekeeping defaults.
  pay_period_cadence?: string | null;
  pay_period_anchor?: string | null;
  timesheet_offset_days?: number | null;
  require_timesheet_approval?: boolean | null;
  facilities: Facility[];
  users: GroupHomeUser[];
  facilities_count?: number;
  users_count?: number;
}

export interface CreateGroupHomeData {
  name: string;
  address?: string;
  state?: string | null;
  phone?: string;
  email?: string;
  adminEmail: string;
  adminName: string;
  shift_auto_clock_out_grace_minutes?: number;
}

/**
 * Fetch all group homes (admin only — GET /admin/group-homes requires the
 * platform-tier group-home:read permission, US-065). Non-platform callers
 * must pass `enabled: false`; the endpoint 403s for them otherwise.
 */
export function useGroupHomes(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["admin", "group-homes"],
    queryFn: async () => {
      const { data } = await api.get("/admin/group-homes");
      return data.data as GroupHome[];
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch single group home with details
 */
export function useGroupHome(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "group-homes", id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/group-homes/${id}`);
      return data.data as GroupHomeDetail;
    },
    enabled: !!id,
  });
}

/**
 * Create new group home with admin user
 */
export function useCreateGroupHome() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupHomeData: CreateGroupHomeData) => {
      const { data } = await api.post("/admin/group-homes", groupHomeData);
      return data.data;
    },
    // US-081: GroupHomeForm catches with handleFormErrors (US-080 DOM-6).
    meta: FORM_MUTATION_META,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "group-homes"] });
    },
  });
}

/**
 * Update group home
 */
export function useUpdateGroupHome() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<GroupHome> & { id: string }) => {
      const { data } = await api.put(`/admin/group-homes/${id}`, updates);
      return data.data;
    },
    // US-081: GroupHomeForm catches with handleFormErrors (US-080 DOM-6).
    meta: FORM_MUTATION_META,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "group-homes"] });
      queryClient.invalidateQueries({
        queryKey: ["admin", "group-homes", variables.id],
      });
    },
  });
}

/**
 * Remove a user's role from a group home.
 * Calls DELETE /api/admin/users/:employeeId/roles?assignment_id=:assignmentId
 * and refreshes the group home detail.
 */
export function useRemoveGroupHomeUser(groupHomeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      employeeId,
      assignmentId,
    }: {
      employeeId: string;
      assignmentId: string;
    }) => {
      const { data } = await api.delete(
        `/admin/users/${employeeId}/roles?assignment_id=${assignmentId}`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "group-homes", groupHomeId],
      });
    },
  });
}

/**
 * Delete group home
 */
export function useDeleteGroupHome() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/admin/group-homes/${id}`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "group-homes"] });
    },
  });
}
